import { useState } from 'react';
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, RedoIcon } from '@nimbus-ds/icons';
import {
  Button,
  Input,
  Label,
  Card,
  Alert,
  Tag,
  Icon,
  Spinner,
  Title,
  Text,
} from '@nimbus-ds/components';
import { customEventsService, GA4ValidationResponse } from '@/api/customEvents';

interface GA4ValidationPanelProps {
  eventId?: string;
  ga4EventName: string;
  onGA4EventNameChange: (name: string) => void;
  validationStatus?: GA4ValidationResponse | null;
  disabled?: boolean;
}

export function GA4ValidationPanel({
  eventId,
  ga4EventName,
  onGA4EventNameChange,
  validationStatus: initialStatus,
  disabled = false,
}: GA4ValidationPanelProps) {
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState<GA4ValidationResponse | null>(
    initialStatus || null
  );
  const [error, setError] = useState<string | null>(null);

  const handleValidate = async () => {
    if (!eventId) {
      setError('Please save the custom event first before validating GA4 binding');
      return;
    }

    if (!ga4EventName) {
      setError('Please enter a GA4 event name');
      return;
    }

    setValidating(true);
    setError(null);

    try {
      const result = await customEventsService.validateGA4(eventId);
      setValidationResult(result);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to validate GA4 binding');
    } finally {
      setValidating(false);
    }
  };

  const getStatusIcon = () => {
    if (!validationResult) return null;

    switch (validationResult.validation_status) {
      case 'valid':
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case 'event_not_found':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-600" />;
      case 'ga4_not_configured':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600" />;
      default:
        return null;
    }
  };

  const getStatusBadge = () => {
    if (!validationResult) return null;

    switch (validationResult.validation_status) {
      case 'valid':
        return <Tag appearance="success">Validated</Tag>;
      case 'event_not_found':
        return <Tag appearance="danger">Not Found</Tag>;
      case 'ga4_not_configured':
        return <Tag appearance="neutral">GA4 Not Configured</Tag>;
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString();
    } catch {
      return dateString;
    }
  };

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center justify-between gap-2">
          <Title as="h3">GA4 Event Binding</Title>
          {validationResult && (
            <div className="shrink-0 whitespace-nowrap">{getStatusBadge()}</div>
          )}
        </div>
        <Text>
          Bind this custom event to an existing Google Analytics 4 event for validation and
          cross-reference.
        </Text>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          {/* GA4 Event Name Input */}
          <div className="space-y-2">
            <Label htmlFor="ga4-event-name">
              GA4 Event Name <Text as="span" color="neutral-textLow">(optional)</Text>
            </Label>
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  id="ga4-event-name"
                  placeholder="e.g., purchase, add_to_cart, page_view"
                  value={ga4EventName}
                  onChange={(e) => onGA4EventNameChange(e.target.value)}
                  disabled={disabled}
                />
              </div>
              <Button
                onClick={handleValidate}
                disabled={!eventId || !ga4EventName || validating || disabled}
                appearance="neutral"
              >
                {validating ? (
                  <Spinner size="small" />
                ) : (
                  <Icon source={<RedoIcon />} color="currentColor" />
                )}
              </Button>
            </div>
            <Text fontSize="caption">
              Must be snake_case, lowercase, max 40 characters (e.g., "add_to_cart", not
              "Add-To-Cart")
            </Text>
          </div>

          {/* Error Alert */}
          {error && (
            <Alert appearance="danger">{error}</Alert>
          )}

          {/* Validation Result */}
          {validationResult && (
            <div className="space-y-3 rounded-lg border bg-muted/50 p-4">
              <div className="flex items-start gap-2">
                {getStatusIcon()}
                <div className="flex-1 space-y-1">
                  <Text fontWeight="medium" color="neutral-textHigh">{validationResult.message}</Text>
                  <Text fontSize="caption">
                    Last validated: {formatDate(validationResult.last_validated)}
                  </Text>
                </div>
              </div>

              {/* Event Count (if valid) */}
              {validationResult.validation_status === 'valid' && (
                <div className="grid grid-cols-2 gap-3 rounded-md bg-background p-3">
                  <div>
                    <Text fontSize="caption">GA4 Event Name</Text>
                    <Text fontWeight="medium" color="neutral-textHigh" className="font-mono">
                      {validationResult.ga4_event_name}
                    </Text>
                  </div>
                  <div>
                    <Text fontSize="caption">7-Day Event Count</Text>
                    <Text fontWeight="medium" color="neutral-textHigh">
                      {validationResult.event_count_7d.toLocaleString()}
                    </Text>
                  </div>
                </div>
              )}

              {/* Guidance based on status */}
              {validationResult.validation_status === 'event_not_found' && (
                <Alert appearance="neutral">
                  The GA4 event "{validationResult.ga4_event_name}" was not found in your GA4
                  property. Make sure the event name matches exactly (case-sensitive) and that the
                  event has fired in the last 7 days.
                </Alert>
              )}

              {validationResult.validation_status === 'ga4_not_configured' && (
                <Alert appearance="neutral">
                  GA4 is not configured for this store. Please configure your GA4 property ID in
                  the store settings before validating event bindings.
                </Alert>
              )}
            </div>
          )}

          {/* Info Box */}
          {!validationResult && !error && (
            <Alert appearance="neutral">
              <strong>How it works:</strong> Enter the exact GA4 event name you want to bind to
              this custom event. We'll validate that it exists in your GA4 property and show you
              the event count from the last 7 days. This binding is read-only and won't modify your
              GA4 data.
            </Alert>
          )}
        </div>
      </Card.Body>
    </Card>
  );
}
